import { useState, useRef, useEffect, useCallback, useContext } from 'react'
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser'
import { BarcodeFormat, DecodeHintType, NotFoundException, ChecksumException, FormatException, Result } from '@zxing/library'
import Spinner from './Spinner'
import { GlobalContext } from '../GlobalContext'
import Button from '../formfields/Button'
import RedAlert from './RedAlert'



// Every 1D/2D format zxing-js can decode on its own (excludes UPC_EAN_EXTENSION,
// which is only ever read as an add-on to an EAN/UPC code, never standalone).
const READABLE_FORMATS: BarcodeFormat[] = [
    BarcodeFormat.QR_CODE,
    BarcodeFormat.MICRO_QR_CODE,
    BarcodeFormat.AZTEC,
    BarcodeFormat.DATA_MATRIX,
    BarcodeFormat.MAXICODE,
    BarcodeFormat.PDF_417,
    BarcodeFormat.CODABAR,
    BarcodeFormat.CODE_39,
    BarcodeFormat.CODE_93,
    BarcodeFormat.CODE_128,
    BarcodeFormat.EAN_8,
    BarcodeFormat.EAN_13,
    BarcodeFormat.ITF,
    BarcodeFormat.RSS_14,
    BarcodeFormat.RSS_EXPANDED,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
]

function formatName(format: BarcodeFormat): string {
    return BarcodeFormat[format].replace(/_/g, ' ')
}

// 'focusMode' is a real, widely-implemented (Chrome/Android) constraint but
// isn't part of TypeScript's DOM lib typings yet.
type ExtendedTrackConstraintSet = MediaTrackConstraintSet & {
    focusMode?: 'continuous' | 'single-shot' | 'manual' | 'none'
}

function buildVideoConstraints(deviceId?: string): MediaStreamConstraints {
    const source: MediaTrackConstraintSet = deviceId
        ? { deviceId: { exact: deviceId } }
        : { facingMode: 'environment' }
    return {
        video: {
            ...source,
            // A bigger frame gives the decoder more pixels to resolve small/dense
            // codes with - most phone cameras default to a much lower profile
            // unless a resolution is explicitly requested. "ideal" is a soft
            // constraint, so this never fails on cameras that can't reach it.
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            advanced: [{ focusMode: 'continuous' } as ExtendedTrackConstraintSet],
        },
    }
}

function getFriendlyErrorMessage(error: unknown, t: (key: string) => string): string {
    const name = (error as { name?: string } | undefined)?.name
    switch (name) {
        case 'NotAllowedError':
            return t('UIElements.BarCodeReader.permissionDenied')
        case 'NotFoundError':
        case 'OverconstrainedError':
            return t('UIElements.BarCodeReader.noCameraFound')
        case 'NotReadableError':
            return t('UIElements.BarCodeReader.cameraInUse')
        case 'SecurityError':
            return t('UIElements.BarCodeReader.insecureConnection')
        default:
            return (error as { message?: string } | undefined)?.message || t('UIElements.BarCodeReader.unableToAccessCamera')
    }
}

function BarCodeReader({
    results, setResults
}:
    {
        results: ScanResult | null
        setResults: React.Dispatch<React.SetStateAction<ScanResult | null>>
    }) {


    const { t } = useContext(GlobalContext)
    const videoRef = useRef<HTMLVideoElement>(null)
    const freezeCanvasRef = useRef<HTMLCanvasElement>(null)
    const readerRef = useRef<BrowserMultiFormatReader | null>(null)
    const controlsRef = useRef<IScannerControls | null>(null)
    const mountedRef = useRef(true)
    const startPendingRef = useRef(false)

    const [isScanning, setIsScanning] = useState(false)
    const [starting, setStarting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
    const [selectedDeviceId, setSelectedDeviceId] = useState<string>('')
    const [torchOn, setTorchOn] = useState(false)
    const [torchSupported, setTorchSupported] = useState(false)

    const cameraSupported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
    const isSecureContext = typeof window === 'undefined' || window.isSecureContext
    const unsupportedReason = !cameraSupported
        ? t('UIElements.BarCodeReader.unsupportedBrowser')
        : !isSecureContext
            ? t('UIElements.BarCodeReader.insecureConnection')
            : null

    const getReader = () => {
        if (!readerRef.current) {
            const hints = new Map<DecodeHintType, unknown>()
            hints.set(DecodeHintType.POSSIBLE_FORMATS, READABLE_FORMATS)
            // Spend more effort per frame trying to find a code - meaningfully
            // improves the hit rate on angled, small, or lower-contrast codes.
            hints.set(DecodeHintType.TRY_HARDER, true)
            readerRef.current = new BrowserMultiFormatReader(hints, {
                // Attempt a new decode as soon as the previous one finishes rather
                // than waiting the library's 500ms default - more attempts per
                // second means more chances to catch a frame that's in focus.
                delayBetweenScanAttempts: 75,
                delayBetweenScanSuccess: 500,
            })
        }
        return readerRef.current
    }

    const stopScanning = useCallback(() => {
        controlsRef.current?.stop()
        controlsRef.current = null
        setIsScanning(false)
        setTorchSupported(false)
        setTorchOn(false)
    }, [])

    const handleDecode = useCallback((result?: Result, err?: unknown) => {
        if (result) {
            const text = result.getText()
            if (!text) return

            const format = formatName(result.getBarcodeFormat())

            // Grab the current frame before stopping - stopScanning() clears the
            // video's srcObject, which would otherwise leave a black square where
            // the camera preview was.
            const video = videoRef.current
            const canvas = freezeCanvasRef.current
            if (video && canvas) {
                canvas.width = video.videoWidth
                canvas.height = video.videoHeight
                canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height)
            }

            setResults({ text, format, timestamp: Date.now() })
            // Release the camera once a code is found - keeping the stream and
            // decode loop running was driving up browser memory use.
            stopScanning()
            return
        }
        // A "not found" / checksum / format exception fires on almost every frame
        // where no code is in view yet - that's normal and not worth surfacing.
        if (err && !(err instanceof NotFoundException) && !(err instanceof ChecksumException) && !(err instanceof FormatException)) {
            console.error(err)
        }
    }, [stopScanning])

    const startScanning = useCallback(async (deviceId?: string) => {
        if (!cameraSupported || !videoRef.current) return
        // Guards against overlapping calls (e.g. React StrictMode's dev mount ->
        // cleanup -> mount firing this twice back to back). Both calls target the
        // same <video> element, and zxing's controls.stop() unconditionally clears
        // whatever stream is currently attached to it - so if both were allowed to
        // run, whichever finished "losing" would blank out the winner's video feed
        // when it cleaned itself up. Simplest fix is to just not let a second
        // start begin while one is still in flight.
        if (startPendingRef.current) return
        startPendingRef.current = true

        setError(null)
        setStarting(true)

        controlsRef.current?.stop()
        controlsRef.current = null

        try {
            const reader = getReader()
            const controls = await reader.decodeFromConstraints(
                buildVideoConstraints(deviceId || undefined),
                videoRef.current,
                (result, err) => handleDecode(result, err)
            )
            if (!mountedRef.current) {
                // Unmounted while the camera was still starting up - stop the
                // stream we just opened rather than leaving it running.
                controls.stop()
                return
            }
            controlsRef.current = controls
            setIsScanning(true)
            setTorchOn(false)
            setTorchSupported(!!controls.switchTorch)

            const activeStream = videoRef.current.srcObject as MediaStream | null
            const activeDeviceId = activeStream?.getVideoTracks()[0]?.getSettings().deviceId
            if (activeDeviceId) setSelectedDeviceId(activeDeviceId)

            // Device labels are only populated once permission has been granted,
            // so we (re-)enumerate here rather than before starting the camera.
            try {
                const videoDevices = await BrowserMultiFormatReader.listVideoInputDevices()
                setDevices(videoDevices)
            } catch {
                // Listing extra cameras is a nice-to-have; scanning still works without it.
            }
        } catch (e) {
            setIsScanning(false)
            setError(getFriendlyErrorMessage(e, t))
        } finally {
            setStarting(false)
            startPendingRef.current = false
        }
    }, [cameraSupported, handleDecode, t])


    const switchCameraToNextDevice = useCallback(() => {
        if (devices.length < 2) return
        const currentIndex = devices.findIndex(d => d.deviceId === selectedDeviceId)
        const nextIndex = (currentIndex + 1) % devices.length
        const nextDeviceId = devices[nextIndex].deviceId
        handleCameraChange(nextDeviceId)
    }, [devices, selectedDeviceId])

    const handleCameraChange = (deviceId: string) => {
        setSelectedDeviceId(deviceId)
        if (isScanning) startScanning(deviceId)
    }

    const rescan = useCallback(() => {
        setResults(null)
        startScanning(selectedDeviceId || undefined)
    }, [startScanning, selectedDeviceId])

    const toggleTorch = useCallback(async () => {
        if (!controlsRef.current?.switchTorch) return
        const next = !torchOn
        try {
            await controlsRef.current.switchTorch(next)
            setTorchOn(next)
        } catch {
            // Torch control is experimental and not guaranteed on every device.
        }
    }, [torchOn])

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text)
        } catch {
            // Clipboard API may be unavailable (e.g. insecure context) - safe to ignore.
        }
    }

    // Start scanning as soon as the camera is available, so the user doesn't
    // have to press "Start scanning" on first load.
    useEffect(() => {
        if (cameraSupported && isSecureContext) {
            startScanning()
        }
    }, [cameraSupported, isSecureContext, startScanning])

    // Release the camera when the component unmounts (e.g. the user switches tabs).
    // Resetting mountedRef true on mount matters under StrictMode, which runs
    // effects mount -> cleanup -> mount in dev; without this, the cleanup's
    // `false` would stick and the second mount's startScanning() would think
    // it was already unmounted, immediately stopping the camera it just opened.
    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
            controlsRef.current?.stop()
            controlsRef.current = null
        }
    }, [])

    return <div className={`w-screen h-screen fixed top-0 left-0 flex flex-col justify-center items-center`}>



 

        {(unsupportedReason || error) ? <div className="w-full h-full flex flex-col justify-center items-center gap-2">
            <div className="w-full h-full max-w-xl flex justify-center items-center">
                <RedAlert>{unsupportedReason || error}</RedAlert>
            </div>
        </div>
            :

            <div className="relative w-full h-full bg-black" style={{}}>
                <video
                    ref={videoRef}
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                />
                <canvas
                    ref={freezeCanvasRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ display: results ? 'block' : 'none' }}
                />
                {!isScanning && !results && (
                    <div className="absolute inset-0 flex items-center justify-center text-white text-sm  text-center px-4">
                        <Spinner>{t('General.loading')}</Spinner>
                    </div>
                )}


                {(isScanning || results) && <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center px-6">


                    <div className="w-full max-w-xl bg-white/70 rounded px-2 py-2">

                        {results && <div className={`w-full flex flex-col items-center justify-center gap-0`}>
                            <p className={`font-righteous text-brandblue text-lg`}>{t('UIElements.BarCodeReader.detected')}</p>
                            <p className={`text-xs font-mono`}>{results.format}</p>
                            <p className={`w-full mt-2 bg-white px-3 py-2 rounded font-mono text-xs`}>{results.text}</p>
                        </div>}

                        {!results && isScanning && <div className="flex items-center justify-center px-6 gap-2">
                            <p className={`animate-pulse text-3xl`}>🔎</p>
                            <p>{t('UIElements.BarCodeReader.lookingForBarcode')}</p>
                        </div>}



                        {((isScanning && devices.length > 1) || results) && <div className={`w-full flex flex-row items-center justify-center gap-2 mt-2`}>
                            {isScanning && devices.length > 1 && <Button
                                onClick={switchCameraToNextDevice}
                                text={` 🔄 ${t('UIElements.Header.switchCamera')}    `}
                                variant={`primary`}
                                size={`small`}
                                fullwidth={false}
                            />}
                            {results && <Button
                                onClick={rescan}
                                text={t('UIElements.BarCodeReader.reset')}
                                variant={`primary`}
                                size={`small`}
                                fullwidth={false}
                            />}
                        </div>}


                    </div>
                </div>}


            </div>

        }




    </div>
}

export default BarCodeReader
